import type { LibraryView } from '@/services/library/library-view'

import { api } from '../client'
import type { ArcgisDashboardCrawl } from './arcgisDashboards.api'

/**
 * One robot as the chatbot sees it — a transcription of that robot's live
 * `kind:"capabilities"` advertisement, mirroring the AI service's `RobotContext`
 * (`services/console/ai/src/tools/types.ts`) and its wire schema
 * (`dto/chatRequest.ts`).
 *
 * IT IS BUILT FROM THE ADVERTISEMENT AND NOTHING ELSE. Not from a list this
 * console keeps, not from `model`, not from the roster. The whole containment
 * story for the robot tools rests on the model only being able to name things
 * the robot itself offered, so the moment this is assembled from anything other
 * than `resolveCapabilities(...)` output, the guards downstream are guarding a
 * list we invented.
 */
export type RobotChatContext = {
  robotId: string
  name: string
  /** DISPLAY ONLY. Never branch on it. */
  model?: string
  online: boolean
  gestures?: Array<{ name: string; label: string; big: boolean }>
  linkcraft?: Array<{ key: string; label: string; big: boolean }>
  expressions?: Array<{ id: number; name: string }>
  /** Present only when the robot advertised a working voice (`tts:true`).
   *  `cancellable` is what lets `robot_stop` refuse instead of promising. */
  speech?: { languages: string[]; maxChars: number; cancellable: boolean }
  telemetry?: { battery?: number | null; posture?: string | null; estopped?: boolean | null }
}

export type AssistantSecurityScanType = 'arcgis' | 'database' | 'external' | 'internal' | 'host'

export interface AssistantSecurityCounts {
  fail: number
  warn: number
  pass: number
  critical: number
}

/**
 * What the /security pages show. Mirrors the server's `securityContextSchema` by hand: a value
 * over its cap rejects the WHOLE chat request, so `clampSecurityContext` cuts it first (target id
 * <= 64, never cut; name <= 200; counts whole numbers 0..100,000; byScanType <= 5).
 */
export interface AssistantSecurityContext {
  view: 'overview' | 'scan_type' | 'target' | 'agents'
  scanType?: AssistantSecurityScanType
  target?: { id: string; name: string }
  counts?: AssistantSecurityCounts
  byScanType?: ({ scanType: AssistantSecurityScanType } & AssistantSecurityCounts)[]
  loading?: boolean
}

/**
 * What the /monitoring pages show. Mirrors the server's `monitoringContextSchema` by hand; a value
 * over its cap rejects the WHOLE chat request, so `clampMonitoringContext` cuts it first.
 */
export interface AssistantMonitoringContext {
  view: 'library' | 'site'
  site?: { id: string; name: string }
  window?: { preset?: '1h' | '6h' | '24h' | '7d' | '30d'; from: string; to: string }
  filters?: { service?: string; user?: string }
  loading?: boolean
}

/** What the /capacity pages show. Mirrors the server's `capacityContextSchema` by hand. */
export interface AssistantCapacityContext {
  view: 'runs' | 'run'
  run?: { id: string; status: string; server: string }
  loading?: boolean
}

/** What Settings → Users and Groups show. Mirrors the server's `settingsContextSchema` by hand. */
export interface AssistantSettingsContext {
  view: 'users' | 'groups'
  users?: {
    id: string
    name: string
    email: string
    role: 'admin' | 'publisher' | 'viewer'
    status: 'active' | 'suspended'
  }[]
  seats?: Partial<Record<'admin' | 'publisher' | 'viewer', { used: number; max: number }>>
  groups?: { id: string; name: string; member_count: number }[]
  open_group?: { id: string; name: string }
  loading?: boolean
  /** The page holds only its first page of users and more exist: counts and membership need `list_users`. */
  users_truncated?: boolean
  /** The same for groups: counts and membership need `list_groups`. */
  groups_truncated?: boolean
}

export interface ChatToolCall {
  id: string
  name: string
  input: unknown
  result: unknown
  roundNumber?: number
}

export type ChatEarlyTerminationReason =
  | 'max_iterations'
  | 'repeated_tool_call'
  | 'no_text_progress'
  | 'repeated_tool_error'
  | 'provider_error'
  | 'user_cancelled'
  | 'awaiting_client_tool'

export interface ChatResponse {
  sessionId: string
  response: string
  toolCalls: ChatToolCall[]
  usage: { inputTokens: number; outputTokens: number; cost: number; model: string }
  continuationRounds: number
  earlyTerminationReason?: ChatEarlyTerminationReason
  totalExecutionTimeMs: number
}

export type AssistantPageKind =
  | 'dashboard'
  | 'map'
  | 'library'
  | 'forms'
  | 'scripts'
  | 'security'
  | 'monitoring'
  | 'capacity'
  | 'settings'
  | 'geocoding'
  | 'home'
  | 'other'

export type AssistantSurface =
  | {
      kind: 'sitewide'
      route: string
      pageKind: AssistantPageKind
      availableActions?: string[]
      /**
       * The dashboard the user is viewing, when one is mounted. The server uses
       * it to build a `dashboardsClient` for a turn that has no chatbot
       * element — AS THIS CALLER, so a dashboard the caller cannot read is
       * refused by the dashboards service, not by our trust in this field.
       */
      dashboardId?: string
    }
  | { kind: 'element'; elementId: string; route: string; pageKind: AssistantPageKind; availableActions?: string[] }

/** What the Library page (list or one item) shows. Mirrors the server's `libraryContextSchema`. */
export interface AssistantLibraryContext {
  view: 'list' | 'item'
  filters?: LibraryView
  visibleCount?: number
  loading?: boolean
  item?: {
    id: string
    type: 'layer' | 'map' | 'dashboard' | 'file' | 'csv_table'
    name: string
    sharingLevel: 'owner' | 'organization' | 'groups' | 'public'
    publishStatus?: string
  }
}

/** A geocoding session's status. Mirrors the server's `GEOCODING_SESSION_STATUSES`. */
export type AssistantGeocodingSessionStatus =
  | 'draft'
  | 'uploading'
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'

/** A step of the geocoding wizard. Mirrors the server's `GEOCODING_WIZARD_STEPS`. */
export type AssistantGeocodingWizardStep = 'source' | 'fields' | 'options' | 'review' | 'processing' | 'complete'

/** One geocoding session as the assistant sees it. */
export interface AssistantGeocodingSession {
  /** At most 64 chars; a longer id is not sent. */
  id: string
  /** At most 200 chars. User text. */
  name: string
  status: AssistantGeocodingSessionStatus
  /** An integer in 1..1000. */
  passNumber: number
  stopRequested?: boolean
  /** Integers in 0..100,000,000. */
  progress?: { total: number; processed: number; successful: number; failed: number }
}

/**
 * What the /geocoding pages show. Mirrors the server's `geocodingContextSchema`
 * by hand (there is no shared package): any value over its cap rejects the
 * WHOLE chat request, so `clampGeocodingContext` cuts it first.
 */
export interface AssistantGeocodingContext {
  view: 'list' | 'wizard' | 'results'
  step?: AssistantGeocodingWizardStep
  session?: AssistantGeocodingSession
  /** At most 20: the rows on screen. */
  sessions?: Array<Pick<AssistantGeocodingSession, 'id' | 'name' | 'status'>>
  loading?: boolean
}

export type AssistantFormStatus = 'draft' | 'published' | 'archived'
export type AssistantFormSharing = 'owner' | 'organization' | 'groups' | 'public'
export type AssistantSubmissionStatus = 'pending' | 'committed' | 'failed' | 'orphaned'

/** One form as the assistant sees it. */
export interface AssistantFormSummary {
  id: string
  name: string
  status: AssistantFormStatus
  sharing: AssistantFormSharing
}

/** One builder question as the assistant sees it. */
export interface AssistantFormField {
  name: string
  label: string
  type: string
  required: boolean
  /** Option values, at most 20. */
  options?: string[]
  /** Compact rule text, at most 300, e.g. "show when result eq fail". */
  rules?: string
  locked?: boolean
}

/**
 * What the /forms list, builder and submissions pages show. Mirrors the server's
 * `formsContextSchema` by hand: any value over its cap rejects the WHOLE chat request,
 * so `clampFormsContext` cuts it first.
 */
export interface AssistantFormsContext {
  view: 'list' | 'builder' | 'submissions'
  /** List: at most 30, the rows on screen. */
  forms?: AssistantFormSummary[]
  /** List: search at most 200. */
  filter?: { archived: boolean; search?: string }
  form?: AssistantFormSummary & {
    /** At most 200, e.g. "new table" / "layer Tree Inventory". */
    target?: string
    dirty?: boolean
    selected?: string | null
    /** Builder: at most 60, plus `fieldCount`. */
    fields?: AssistantFormField[]
    fieldCount?: number
  }
  /** At most 25. */
  submissions?: { id: string; status: AssistantSubmissionStatus; submittedAt: string }[]
  statusFilter?: AssistantSubmissionStatus
  total?: number
  openSubmissionId?: string
  loading?: boolean
}

/** Where the open notebook's interactive session stands, as the assistant sees it. */
export type AssistantScriptSession = 'none' | 'starting' | 'ready' | 'held_by_other'

/**
 * What the /scripts list and a script's page show. Mirrors the server's `scriptsContextSchema`
 * by hand: any value over its cap rejects the WHOLE chat request, so `clampScriptsContext` cuts
 * it first (ids <= 64, never cut; names <= 200; notebooks <= 50; cellCount <= 10,000; activeCell <= 9,999).
 */
export interface AssistantScriptsContext {
  view: 'list' | 'notebook'
  /** List: at most 50. */
  notebooks?: { id: string; name: string; scheduleOn: boolean; lastRunStatus?: string }[]
  notebook?: {
    id: string
    name: string
    session: AssistantScriptSession
    lastRun?: { id: string; status: string }
    /** Ready session only: the JupyterLab bridge answered. The rest is from its last snapshot. */
    bridgeReady?: boolean
    /** Ready session only: `unavailable` once the bridge's hellos ran out unanswered. */
    bridge?: 'connecting' | 'ready' | 'unavailable'
    /** <= 10,000. */
    cellCount?: number
    /** 0-based, <= 9,999; absent when no cell is active. */
    activeCell?: number
    /** Unsaved edits in the open notebook. */
    dirty?: boolean
  }
}

/** A picture or PDF the user attached to a chat turn. Only the turn being sent carries the bytes. */
export type ChatAttachment = { kind: 'image'; dataUrl: string } | { kind: 'document'; name: string; dataUrl: string }

/** A file the browser already uploaded to the Library for this turn; the model gets its id. */
export interface ChatAttachedFile {
  id: string
  title: string
  fileType: string
}

/**
 * An attachment as the transcript shows it: a picture by its `src` (the data URL on a live turn, the
 * stored picture's URL on a resumed one; absent when the server kept no URL), a PDF by name only.
 */
export type ChatMessageAttachment = { kind: 'image'; src?: string } | { kind: 'document'; name: string }

/** An attachment as a stored session returns it: pictures as a URL, PDFs by name. */
export type StoredChatAttachment = { kind: 'image'; url?: string } | { kind: 'document'; name: string }

export interface ChatRequest {
  /** Absent on a sitewide turn. */
  elementId?: string
  surface?: AssistantSurface
  dashboardId?: string
  sessionId?: string
  /** Required unless `toolResults` carries a client-tool continuation (Task 16). */
  message?: string
  /** Client-executed tool result(s) completing a server-issued `client_tool_request`. */
  toolResults?: Array<{ toolCallId: string; result: unknown }>
  /** The run this continuation belongs to — echoes the server's `client_tool_request.runId`. */
  runId?: string
  context?: {
    /** Library page: the list's filters and count, or the one item on screen. */
    library?: AssistantLibraryContext
    /** Geocoding pages: the wizard's step and session, the sessions list, or one run's results. */
    geocoding?: AssistantGeocodingContext
    /** Forms pages: the list, the builder with its questions, or a form's submissions. */
    forms?: AssistantFormsContext
    /** Scripts pages: the list, or one script with its session and last run. */
    scripts?: AssistantScriptsContext
    /** Security pages: the overview, a scan type, one target, or the agents page. */
    security?: AssistantSecurityContext
    /** Monitoring pages: the site library, or one site with its window and filters. */
    monitoring?: AssistantMonitoringContext
    /** Capacity pages: the run list, or one run. */
    capacity?: AssistantCapacityContext
    /** Settings → Users / Groups: the visible users or groups, seat usage, an open group. */
    settings?: AssistantSettingsContext
    mapState?: {
      center: [number, number]
      zoom: number
      visibleLayerIds: string[]
      engineType: 'openlayers' | 'arcgis'
      /** Whether the user is in 2D or 3D. Omitted on legacy/2D-only clients. */
      viewType?: '2d' | '3d'
      /** Live 3D camera state. Present only when `viewType === '3d'`. */
      camera?: {
        position: [number, number, number]
        heading: number
        tilt: number
      }
      /** Slides available on the saved map, for play_slide tool resolution. */
      slides?: Array<{ id: string; title: string }>
      /** Layer groups — present when the map has at least one group node. */
      groups?: Array<{ id: string; title: string; childLayerIds: string[] }>
      /** 3D scene environment — present when the user is in 3D. */
      environment?: {
        timeOfDay?: string
        weather?: string
        atmosphere?: 'on' | 'off'
        waterReflections?: boolean
        sunPosition?: { azimuth_deg: number; elevation_deg: number }
      }
      /** Map page: identity, unsaved changes and whether the user may save. */
      map?: {
        id: string | null
        name: string | null
        canSave: boolean
        unsavedChanges: string[]
        isNew: boolean
      }
      /** Map page: the active basemap. */
      basemap?: { id: string | null; name: string | null }
      /** Map page: enabled ArcGIS widgets (ArcGIS engine only). */
      widgets?: string[]
    }
    layerSamples?: Array<{
      layerId: string
      layerName: string
      layerType: 'postgresql' | 'wfs' | 'arcgis_feature' | 'geojson' | 'csv_table' | 'wms'
      fields?: Array<{ name: string; type: string }>
      sample?: Record<string, unknown>
      /** Source URL — used to resolve engine label (ArcGIS Online vs Enterprise). */
      sourceUrl?: string
      /** Map page: current visibility, opacity (0-1) and one-line style summary. */
      visible?: boolean
      opacity?: number
      style?: string
    }>
    autoHighlightResults?: boolean
    dashboardElements?: Array<{ id: string; type: string; title: string }>
    /** On-map CCTV cameras (catalog ∩ map cctv_id points) for the cctv_select tool. */
    cctvCameras?: Array<{
      cameraId: string
      name: string
      kind: 'hik' | 'rtsp' | 'onvif'
      cctvId?: string
      lng?: number
      lat?: number
      layerId?: string
      layerName?: string
      ptzCapable?: boolean
    }>
    /** The currently-selected (followed) camera id, or null. */
    currentCctvSelection?: string | null
    /** Floor-aware buildings visible on the map (get_floors / query_floor / floorsPrompt). */
    floors?: Array<{
      buildingId: string
      buildingName: string
      levels: Array<{ value: number; label?: string }>
      floorField: string
      layerId: string
      activeFloor: number | null
    }>
    /** Server-side crawl of embedded ArcGIS Dashboard(s) on this map, for the ArcGIS-dashboard query tool. */
    arcgisDashboards?: ArcgisDashboardCrawl[]
    /**
     * Robots on this dashboard and WHAT EACH ONE SAYS IT CAN DO — the
     * anti-hallucination substrate for every robot tool. Assembled by
     * `DashboardBridgeProvider` from each robot's live `kind:"capabilities"`
     * advertisement (`RobotChatContext`), and mirrored server-side by
     * `robotContextSchema` in the AI service's `dto/chatRequest.ts`. The model
     * may only name something that appears here, because what appears here is
     * what the robot itself said.
     */
    robots?: RobotChatContext[]
  }
  provider?: 'claude' | 'openai' | 'ollama' | 'bedrock'
  model?: string
  /** Full data: URL of a captured still (capture-still describe flow). */
  image?: { dataUrl: string }
  /** Pictures and PDFs attached in the assistant's picker. At most 5. */
  attachments?: ChatAttachment[]
  /** Library files uploaded from the picker for this turn. At most 5. */
  attachedFiles?: ChatAttachedFile[]
}

export interface ChatSession {
  _id: string
  title?: string
  messages: Array<{
    // `tool_result` rows exist so the model gets the full sequence back on
    // resume/rebuild; the resume mapping (DashboardChatbot.tsx) filters them
    // out of what's actually rendered — the server does NOT filter them out.
    // The resume mapping ALSO filters out `assistant` rows with `content ===
    // ''` — the companion tool_use step a tool-bearing turn persists
    // alongside its `tool_result` rows — or a resumed conversation shows an
    // empty grey bubble per tool-bearing turn.
    role: 'user' | 'assistant' | 'tool_result'
    content: string
    timestamp: string
    tokenCount?: number
    image?: string
    /** Markers for what the user attached. Optional: older sessions (and older servers) omit it. */
    attachments?: StoredChatAttachment[]
    attachedFiles?: ChatAttachedFile[]
    /**
     * Tool calls the assistant emitted on this row — mirrors
     * `ChatbotSession.schema.ts`'s `ChatbotMessage.toolCalls` verbatim: only
     * `{id, name, input}`, no `result`. Set only on the `role === 'assistant',
     * content === ''` companion row a tool-bearing turn persists (filtered
     * out of rendering above). The resume mapping reads it off that row and
     * re-attaches it to the turn's closing text message, resolving each
     * call's `result` from the matching `tool_result` row's `toolCallId` —
     * that row's `content` is the only place the executed result (including
     * geometry) survives persistence.
     */
    toolCalls?: Array<{ id: string; name: string; input: unknown }>
    /** The provider `tool_use` id this row answers. Set only on `tool_result` rows. */
    toolCallId?: string
  }>
  status: 'active' | 'archived' | 'deleted'
  totalTokensUsed?: number
  createdAt: string
  updatedAt: string
}

export const chatbotApi = {
  sendMessage: (req: ChatRequest) => api.post<ChatResponse>('/ai/chat', req, { timeout: 60000 }),
  getSession: (id: string) => api.get<ChatSession>(`/ai/sessions/${id}`),
  getActiveSession: (opts: { surface: 'element' | 'sitewide'; elementId?: string }) =>
    api.get<ChatSession>('/ai/sessions/active', {
      params: opts.surface === 'sitewide' ? { surface: 'sitewide' } : { surface: 'element', elementId: opts.elementId },
    }),
  listSessions: () => api.get<ChatSession[]>('/ai/sessions'),
  deleteSession: (id: string) => api.delete<void>(`/ai/sessions/${id}`),
}
